import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { MapPin, Phone, MessageCircle } from "lucide-react";
import { locations } from "@/data/locations";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Service Coverage in Thailand — 127 Locations | NYC Language Institute";
const DESC =
  "NYC Legal serves Bangkok HQ + 50 districts and 76 provinces across Thailand. Same-day Bangkok pickup. Talk to a specialist today; no obligation, fast reply.";

export const Route = createFileRoute("/en/locations/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/locations", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/locations", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/locations", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/locations", "en")},
      { property: "og:url", content: `${SITE}/en/locations` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/locations` },
      ...hreflangLinks("/locations", "/en/locations"),
    ],
    scripts: [speakableSchema(), 
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "NYC Legal Service Locations",
          numberOfItems: locations.length,
          itemListElement: locations.slice(0, 50).map((l, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: l.nameEn,
            url: `${SITE}/locations/${l.slug}`,
          })),
        }),
      },
    ],
  }),
  component: EnLocationsPage,
});

function EnLocationsPage() {
  const byRegion = locations.reduce<Record<string, typeof locations>>((acc, l) => {
    const k = l.region || "Other";
    (acc[k] ||= []).push(l);
    return acc;
  }, {});
  const regions = Object.keys(byRegion).sort();

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">← EN home</Link>
            <span className="text-white/30">·</span>
            <Link to="/locations" className="text-white/80 hover:text-amber-300">ภาษาไทย</Link>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold sm:text-5xl">
            Coverage across <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">all of Thailand</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Bangkok HQ in Lat Phrao 95 · 50 Bangkok districts · 76 provinces · International DHL & FedEx.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66832494999" data-track-zone="en-locations-hero" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" data-track-zone="en-locations-hero" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl space-y-10">
          {regions.map((r) => (
            <div key={r}>
              <h2 className="text-lg font-bold text-amber-200">{r}</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {byRegion[r].map((l) => (
                  <Link
                    key={l.slug}
                    to="/locations/$slug"
                    params={{ slug: l.slug }}
                    className="group flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 transition hover:border-amber-400/40 hover:bg-white/[0.06]"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white/90">{l.nameEn}</p>
                      <p className="truncate text-xs text-white/80">{l.nameTh}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
